import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { Plus, Edit, Trash2, Users, ArrowLeft, Save, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import Swal from "sweetalert2";
import {
    createPosition,
    deletePosition,
    fetchPositions,
    updatePosition
} from "../../services/crmServices/positionService";
import { Select } from "../../components/ui/select";
import { floors, roles } from "../../services/common/constants";
import { getRoleLabel, getFloorLabel } from "../../services/common/util";

export function CustomPositionManagement() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [positions, setPositions] = useState([]);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        role: "",
        floor: "",
        isActive: true,
        permissions: {
            leads: {
                view: false,
                create: false,
                update: false,
                delete: false,
                viewEmail: false,
                viewPhone: false,
                viewActionHistory: false
            },
            staff: {
                view: false,
                create: false,
                update: false,
                delete: false
            },
            desks: {
                view: false,
                create: false,
                update: false,
                delete: false
            },
            gridLeadsColumns: {
                source: true,
                affiliate: true,
                manager: true,
                registered: true,
                modified: true,
                desk: true,
                floorManager: true,
                lastComment: true,
                lastCommDate: true,
                lastCommManager: true,
                age: true
            },
            editLeadFields: {
                firstName: false,
                lastName: false,
                userName: false,
                email: false,
                phone: false,
                secondPhone: false,
                status: false,
                country: false,
                city: false,
                address: false,
                source: false,
                extra: false
            },
            voip: {
                commpeak: false,
                voiso: false,
                coperato: false,
                squaretalk: false
            }
        }
    });

    useEffect(() => {
        if (user?.position?.role !== "admin") {
            navigate("/");
        }
        initPage();
    }, [user, navigate]);

    const initPage = () => {
        fetchPositions()
            .then((response) => {
                if (response?.data.length > 0) {
                    setPositions(response.data);
                }
            })
            .catch((error) => {
                console.error("Error fetching positions:", error);
                toast.error("Failed to load positions. Please try again later.");
            });
    };
    const handleCreate = () => {
        setFormData({
            name: "",
            role: "",
            floor: "",
            isActive: true,
            permissions: {
                leads: {
                    view: true,
                    create: false,
                    update: true,
                    delete: false,
                    viewEmail: true,
                    viewPhone: true,
                    viewActionHistory: true
                },
                staff: {
                    view: false,
                    create: false,
                    update: false,
                    delete: false
                },
                desks: {
                    view: false,
                    create: false,
                    update: false,
                    delete: false
                },
                gridLeadsColumns: {
                    source: true,
                    affiliate: true,
                    manager: true,
                    registered: true,
                    modified: true,
                    desk: true,
                    floorManager: true,
                    lastComment: true,
                    lastCommDate: true,
                    lastCommManager: true
                },
                editLeadFields: {
                    firstName: true,
                    lastName: true,
                    userName: true,
                    email: false,
                    phone: false,
                    secondPhone: false,
                    status: true,
                    country: false,
                    city: false,
                    address: false,
                    source: false,
                    extra: false
                },
                voip: {
                    commpeak: false,
                    voiso: false,
                    coperato: false,
                    squaretalk: false
                }
            }
        });
        setShowCreateModal(true);
    };

    const handleEdit = (position) => {
        // if (position.isDefault) {
        //     toast.error("Default positions cannot be edited");
        //     return;
        // }
        setSelectedPosition(position);
        setFormData({
            name: position.name,
            role: position.role || "",
            floor: position.floor || "",
            permissions: {
                ...position.permissions,
                voip: {
                    commpeak: position.permissions?.voip?.commpeak || false,
                    voiso: position.permissions?.voip?.voiso || false,
                    coperato: position.permissions?.voip?.coperato || false,
                    squaretalk: position.permissions?.voip?.squaretalk || false
                }
            }
        });
        setShowEditModal(true);
    };

    const handleDelete = (position) => {
        if (position.isDefault) {
            toast.error("Default positions cannot be deleted");
            return;
        }

        if (position.userCount > 0) {
            toast.error(
                `Cannot delete position "${position.name}" because ${position.userCount} users are assigned to it`
            );
            return;
        }

        Swal.fire({
            title: "Are you sure?",
            text: `This will permanently delete the "${position.name}" position.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626"
        }).then((result) => {
            if (result.isConfirmed) {
                deletePosition(position._id).then((response) => {
                    if (response?.status === 200) {
                        toast.success("Position deleted successfully!");
                        initPage();
                    } else {
                        toast.error("Failed to delete position. Please try again later.");
                    }
                });
            }
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name) {
            toast.error("Please enter Position Name");
            return;
        }

        if (!formData.role) {
            toast.error("Please select Role Type");
            return;
        }

        if (formData.role !== "admin" && formData.role !== "generalManager" && !formData.floor) {
            toast.error("Please select Floor");
            return;
        }

        // Check for duplicates
        const isDuplicate = positions.find(
            (position) =>
                position.name.toLowerCase() === formData.name.toLowerCase() && position._id !== selectedPosition?._id
        );

        if (isDuplicate) {
            toast.error("A position with this name already exists");
            return;
        }

        if (selectedPosition) {
            const updatedPosition = {
                _id: selectedPosition._id,
                ...formData
            };
            updatePosition(updatedPosition).then((response) => {
                if (response?.status === 200) {
                    toast.success("Position updated successfully!");
                    setShowEditModal(false);
                    initPage();
                } else {
                    toast.error("Failed to update position. Please try again later.");
                }
            });
        } else {
            createPosition(formData).then((response) => {
                if (response?.status === 200) {
                    toast.success("Position created successfully!");
                    setShowCreateModal(false);
                    initPage();
                } else {
                    toast.error("Failed to create position. Please try again later.");
                }
            });
        }
        setSelectedPosition(null);
    };

    const handlePermissionChange = (category, permission, checked) => {
        setFormData((prev) => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [category]: {
                    ...prev.permissions[category],
                    [permission]: checked
                }
            }
        }));
    };

    const handleBack = () => {
        navigate("/");
    };

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Button variant="outline" onClick={handleBack} size="icon">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <h1 className="text-2xl font-bold text-gray-900">Custom Position Management</h1>
                    </div>
                    <Button onClick={handleCreate}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Position
                    </Button>
                </div>
                <p className="text-gray-600 mt-2">
                    Create and manage custom positions with specific permissions. Default positions cannot be modified.
                </p>
            </div>

            <div className="rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Actions</TableHead>
                            <TableHead>Position Name</TableHead>
                            <TableHead>Role Type</TableHead>
                            <TableHead>Floor</TableHead>
                            <TableHead>Permissions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {positions.map((position) => (
                            <TableRow key={position._id}>
                                <TableCell>
                                    <div className="flex space-x-1">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleEdit(position)}
                                            disabled={position.isDefault}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleDelete(position)}
                                            disabled={position.isDefault || position.userCount > 0}
                                        >
                                            <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                    </div>
                                </TableCell>
                                <TableCell className="font-medium">{position.name}</TableCell>
                                <TableCell className="font-medium">{getRoleLabel(position.role)}</TableCell>
                                <TableCell className="font-medium">{getFloorLabel(position.floor)}</TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {position.permissions.leads.view && (
                                            <Badge variant="outline" className="text-xs">
                                                View Leads
                                            </Badge>
                                        )}
                                        {position.permissions.leads.create && (
                                            <Badge variant="outline" className="text-xs">
                                                Create Leads
                                            </Badge>
                                        )}
                                        {position.permissions.leads.update && (
                                            <Badge variant="outline" className="text-xs">
                                                Update Leads
                                            </Badge>
                                        )}
                                        {position.permissions.staff.view && (
                                            <Badge variant="outline" className="text-xs">
                                                View Staff
                                            </Badge>
                                        )}
                                        {position.permissions.desks.view && (
                                            <Badge variant="outline" className="text-xs">
                                                View Desks
                                            </Badge>
                                        )}
                                        {(position.permissions.voip?.commpeak ||
                                            position.permissions.voip?.voiso ||
                                            position.permissions.voip?.coperato ||
                                            position.permissions.voip?.squaretalk) && (
                                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                                VOIP Access
                                            </Badge>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Create/Edit Modal */}
            <Dialog
                open={showCreateModal || showEditModal}
                onOpenChange={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedPosition(null);
                }}
            >
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selectedPosition ? "Edit Position" : "Create New Position"}</DialogTitle>
                        <DialogDescription>
                            {selectedPosition
                                ? "Update the position information and permissions below."
                                : "Create a new position with specific permissions for your organization."}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Position Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., Senior Sales Agent"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="role">Role Type *</Label>
                                <Select
                                    value={formData.role}
                                    onChange={(value) => setFormData((prev) => ({ 
                                        ...prev, 
                                        role: value,
                                        floor: (value === "admin" || value === "generalManager") ? "" : prev.floor
                                    }))}
                                    options={roles}
                                    placeholder="Select Role Type"
                                    required
                                />
                            </div>
                            {formData.role && formData.role !== "admin" && formData.role !== "generalManager" && (
                                <div className="md:col-span-2">
                                    <Label htmlFor="floor">Floor *</Label>
                                    <Select
                                        value={formData.floor}
                                        onChange={(value) => setFormData((prev) => ({ ...prev, floor: value }))}
                                        options={floors}
                                        placeholder="Select Floor"
                                        required
                                    />
                                </div>
                            )}
                            {formData.role === "generalManager" && (
                                <div className="md:col-span-2">
                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                            <strong>General Manager</strong> oversees both Conversion and Retention floors. No specific floor assignment needed.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Permissions */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <Shield className="h-5 w-5 mr-2" />
                                    Permissions
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Lead Permissions */}
                                <div>
                                    <h4 className="font-medium mb-3">Lead Permissions</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {Object.keys(formData.permissions.leads).map((permission) => (
                                            <div key={permission} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`leads-${permission}`}
                                                    checked={formData.permissions.leads[permission]}
                                                    onCheckedChange={(checked) =>
                                                        handlePermissionChange("leads", permission, checked)
                                                    }
                                                />
                                                <Label htmlFor={`leads-${permission}`} className="text-sm capitalize">
                                                    {permission.replace(/([A-Z])/g, " $1").trim()}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Staff Permissions */}
                                <div>
                                    <h4 className="font-medium mb-3">Staff Permissions</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {Object.keys(formData.permissions.staff).map((permission) => (
                                            <div key={permission} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`staff-${permission}`}
                                                    checked={formData.permissions.staff[permission]}
                                                    onCheckedChange={(checked) =>
                                                        handlePermissionChange("staff", permission, checked)
                                                    }
                                                />
                                                <Label htmlFor={`staff-${permission}`} className="text-sm capitalize">
                                                    {permission}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Desk Permissions */}
                                <div>
                                    <h4 className="font-medium mb-3">Desk Permissions</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {Object.keys(formData.permissions.desks).map((permission) => (
                                            <div key={permission} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`desk-${permission}`}
                                                    checked={formData.permissions.desks[permission]}
                                                    onCheckedChange={(checked) =>
                                                        handlePermissionChange("desks", permission, checked)
                                                    }
                                                />
                                                <Label htmlFor={`desk-${permission}`} className="text-sm capitalize">
                                                    {permission}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Lead Field Edit Permissions */}
                                <div>
                                    <h4 className="font-medium mb-3">Lead Field Edit Permissions</h4>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Control which lead fields this position can edit when they have lead update
                                        permissions.
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {Object.keys(formData.permissions.editLeadFields).map((field) => (
                                            <div key={field} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`editLeadFields-${field}`}
                                                    checked={formData.permissions.editLeadFields[field]}
                                                    onCheckedChange={(checked) =>
                                                        handlePermissionChange("editLeadFields", field, checked)
                                                    }
                                                />
                                                <Label
                                                    htmlFor={`editLeadFields-${field}`}
                                                    className="text-sm capitalize"
                                                >
                                                    {field.replace(/([A-Z])/g, " $1").trim()}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Grid Leads Columns */}
                                <div>
                                    <h4 className="font-medium mb-3">Grid Leads Column Visibility</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {Object.keys(formData.permissions.gridLeadsColumns).map((column) => (
                                            <div key={column} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`columns-${column}`}
                                                    checked={formData.permissions.gridLeadsColumns[column]}
                                                    onCheckedChange={(checked) =>
                                                        handlePermissionChange("gridLeadsColumns", column, checked)
                                                    }
                                                />
                                                <Label htmlFor={`columns-${column}`} className="text-sm capitalize">
                                                    {column.replace(/([A-Z])/g, " $1").trim()}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* VOIP Permissions */}
                                <div>
                                    <h4 className="font-medium mb-3">VOIP Services</h4>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Control which VOIP services this position can access for voice calling
                                        functionality.
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {Object.keys(formData.permissions.voip).map((service) => (
                                            <div key={service} className="flex items-center space-x-2">
                                                <Switch
                                                    id={`voip-${service}`}
                                                    checked={formData.permissions.voip[service]}
                                                    onCheckedChange={(checked) =>
                                                        handlePermissionChange("voip", service, checked)
                                                    }
                                                />
                                                <Label htmlFor={`voip-${service}`} className="text-sm capitalize">
                                                    {service}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
                                        <h4 className="font-medium text-blue-900 mb-2">VOIP Permission Notes</h4>
                                        <ul className="text-sm text-blue-800 space-y-1">
                                            <li>• These permissions control access to voice calling features</li>
                                            <li>
                                                • Users need corresponding VOIP IDs configured to use these services
                                            </li>
                                            <li>• System administrators must configure API keys in System Settings</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h4 className="font-medium text-blue-900 mb-2">Position Usage</h4>
                            <ul className="text-sm text-blue-800 space-y-1">
                                <li>• This position will be available when creating or editing staff members</li>
                                <li>• Users assigned to this position will inherit all selected permissions</li>
                                <li>• Field-level permissions only apply when the user has lead update permissions</li>
                                <li>• Custom positions can be edited or deleted (if no users are assigned)</li>
                            </ul>
                        </div>

                        <div className="flex space-x-3 pt-4">
                            <Button type="submit" className="flex-1">
                                <Save className="h-4 w-4 mr-2" />
                                {selectedPosition ? "Update Position" : "Create Position"}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    setSelectedPosition(null);
                                }}
                                className="flex-1"
                            >
                                <X className="h-4 w-4 mr-2" />
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
